import type { components } from "@/api-schema";
import { getTokenProvider } from "@/config/axios";
import { config } from "@/config/configuration";
import { CORRELATION_ID_HEADER } from "@/config/correlation-id";
import { fetchEventSource, type EventSourceMessage } from "@microsoft/fetch-event-source";
import { useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

const STREAM_PATH = "/v1/notification/stream";
const MAX_RETRY_MS = 30_000;

export type StreamNotification = components["schemas"]["NotificationEventDto"];

/** Thrown to stop reconnecting (e.g. the feature was disabled server-side). */
class FatalStreamError extends Error {}

interface UseNotificationStreamOptions {
  enabled: boolean;
  onNotification: (notification: StreamNotification) => void;
}

/**
 * Holds an SSE connection to the notification stream while `enabled`, injecting a fresh bearer
 * token and correlation ID on every (re)connect so token refresh and reconnect are handled transparently.
 */
export function useNotificationStream({ enabled, onNotification }: UseNotificationStreamOptions): void {
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const controller = new AbortController();
    let attempt = 0;

    const authFetch: typeof fetch = async (input, init) => {
      const provider = getTokenProvider();
      const headers = new Headers(init?.headers);
      headers.set(CORRELATION_ID_HEADER, uuidv4());

      if (provider) {
        const { apiToken } = await provider();
        headers.set("Authorization", `Bearer ${apiToken}`);
      }

      return fetch(input, { ...init, headers });
    };

    void fetchEventSource(`${config.apiEndpoint}${STREAM_PATH}`, {
      fetch: authFetch,
      signal: controller.signal,
      openWhenHidden: true,
      async onopen(response) {
        if (response.ok) {
          attempt = 0;
          return;
        }

        if (response.status === 403) {
          throw new FatalStreamError("Notification stream disabled");
        }

        throw new Error(`Notification stream failed to open: ${response.status}`);
      },
      onmessage(event: EventSourceMessage) {
        if (!event.data) {
          return;
        }

        try {
          onNotificationRef.current(JSON.parse(event.data) as StreamNotification);
        } catch {
          // ignore malformed frames
        }
      },
      onerror(error) {
        if (error instanceof FatalStreamError) {
          throw error;
        }

        attempt += 1;

        return Math.min(1000 * 2 ** attempt, MAX_RETRY_MS);
      },
    }).catch(() => {
      // stream aborted or stopped; nothing to recover
    });

    return () => controller.abort();
  }, [enabled]);
}
